import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { merge, of, catchError, finalize, map, repeat, switchMap, takeUntil, filter } from 'rxjs';
import { LoadersFacade } from '@app/store/loader';
import { selectFeatures } from '@app/store/user';
import { HttpService } from '../services/http.service';
import { shippingAddressesActions } from '../actions/shipping-addresses.actions';
import { selectCurrent } from '../selectors/shipping-addresses.selector';
import { ShippingAddress } from '../models';

@Injectable()
export class ShippingAddressesEffects {
    private loadersFacade = inject(LoadersFacade);
    private actions$ = inject(Actions);
    private httpService = inject(HttpService);
    private store = inject(Store);

    private stopConditions$ = merge(this.actions$.pipe(ofType(shippingAddressesActions.clear)));

    getShippingAddresses$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(shippingAddressesActions.getAddresses),
            switchMap(() => {
                this.loadersFacade.add('get-shipping-addresses');
                return this.httpService.getAddresses().pipe(
                    map(([current, items]) => shippingAddressesActions.getAddressesSuccess({ current, items })),
                    catchError(() => of(shippingAddressesActions.getAddressesError())),
                    finalize(() => this.loadersFacade.delete('get-shipping-addresses')),
                );
            }),
            takeUntil(this.stopConditions$),
            repeat(),
        );
    });

    setShippingAddress$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(shippingAddressesActions.setAddress),
            switchMap((action) => {
                this.loadersFacade.add('set-shipping-addresses');
                return this.httpService.setAddress(action.value).pipe(
                    map(() => shippingAddressesActions.setAddressSuccess({ code: action.value.code })),
                    catchError(() => of(shippingAddressesActions.setAddressError())),
                    finalize(() => this.loadersFacade.delete('set-shipping-addresses')),
                );
            }),
            takeUntil(this.stopConditions$),
            repeat(),
        );
    });

    getDeliveryDaysTriggers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(shippingAddressesActions.setAddressSuccess, shippingAddressesActions.getAddressesSuccess),
            map(() => shippingAddressesActions.getDeliveryInfo()),
        );
    });

    getDeliveryDays$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(shippingAddressesActions.getDeliveryInfo),
            concatLatestFrom(() => [
                this.store.select(selectCurrent).pipe(filter((data): data is ShippingAddress => data !== null)),
                this.store
                    .select(selectFeatures)
                    .pipe(filter((features) => features.some((feature) => feature === 'delivery-info' || feature === 'logistic-minimum-from-street-code'))),
            ]),
            switchMap(([, shippingAddress]) => {
                return this.httpService.getStreetCodeData(shippingAddress.streetCode).pipe(
                    map((data) => shippingAddressesActions.getDeliveryInfoSuccess({ data })),
                    catchError(() => of(shippingAddressesActions.getDeliveryInfoError())),
                );
            }),
        );
    });
}
